import { Globe, FlaskConical, Layers, Wrench, AlertTriangle } from 'lucide-react';
import { useResolvedEnvironment, ENVIRONMENTS } from '@/lib/appEnvironment';

const ICONS = {
  globe: Globe,
  flask: FlaskConical,
  layers: Layers,
  wrench: Wrench,
  alert: AlertTriangle,
};

/**
 * EnvironmentRibbon — an unmistakable badge telling operators which environment
 * they are viewing (PRODUCTION / PREVIEW / STAGING / DEVELOPMENT). Rendered on
 * every admin/host surface so no one acts on the wrong environment. Carries
 * explicit TEXT + an icon (never color alone). Exposes no secrets. Reads the
 * SAME resolved environment as ProductionActionGuard.
 *
 * Props:
 *   className  extra wrapper classes
 *   compact    when true, hides the sub-label (tight spaces)
 */
export default function EnvironmentRibbon({ className = '', compact = false }) {
  const env = useResolvedEnvironment();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const cfg = env || ENVIRONMENTS.unknown;
  const Icon = ICONS[cfg.icon] || AlertTriangle;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ring-1 ring-inset px-2.5 py-1 text-[11px] font-black uppercase tracking-wider select-none shadow-sm ${cfg.classes} ${className}`}
      data-testid="environment-ribbon"
      data-env={cfg.key}
      title={`${cfg.label} — ${cfg.sub} (${host || 'unknown host'})`}
      role="status"
      aria-label={`Environment: ${cfg.label}. ${cfg.sub}.`}
    >
      <span className="relative inline-flex w-2 h-2 flex-shrink-0">
        {cfg.pulse && (
          <span className={`absolute inline-flex h-full w-full rounded-full ${cfg.dot} opacity-60 animate-ping`} />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${cfg.dot}`} />
      </span>
      <Icon className="w-3 h-3 flex-shrink-0" strokeWidth={2.6} />
      <span className="leading-none">{cfg.label}</span>
      {!compact && (
        <span className="hidden lg:inline font-bold normal-case tracking-normal opacity-90 border-l border-white/30 pl-1.5 ml-0.5">
          {cfg.sub}
        </span>
      )}
    </span>
  );
}
